import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';

export type NotaContenido = components['schemas']['ContentNote'];
export type CasoLigado = components['schemas']['ContentLink'];
export type TipoNota = components['schemas']['ContentNoteCreateBody']['contentType'];

export interface Destino {
  contentType: TipoNota;
  contentId: number;
}

const err = (e: unknown) => toast.error(mensajeDe(e));

export function useNotas(d: Destino) {
  return useQuery({
    queryKey: ['contenido', 'notas', d.contentType, d.contentId],
    queryFn: () =>
      unwrap<NotaContenido[]>(
        api.GET('/api/v1/admin/content-notes', {
          params: { query: { contentType: d.contentType, contentId: d.contentId } },
        }),
      ),
  });
}

export function useNotasMutaciones(d: Destino) {
  const qc = useQueryClient();
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['contenido'] });
  return {
    crear: useMutation({
      mutationFn: (body: string) =>
        unwrap(api.POST('/api/v1/admin/content-notes', { body: { ...d, body } })),
      onSuccess: refrescar,
      onError: err,
    }),
    editar: useMutation({
      mutationFn: ({ id, body }: { id: number; body: string }) =>
        unwrap(
          api.PATCH('/api/v1/admin/content-notes/{id}', {
            params: { path: { id: String(id) } },
            body: { body },
          }),
        ),
      onSuccess: refrescar,
      onError: err,
    }),
    borrar: useMutation({
      mutationFn: (id: number) =>
        unwrap(
          api.DELETE('/api/v1/admin/content-notes/{id}', {
            params: { path: { id: String(id) } },
          }),
        ),
      onSuccess: refrescar,
      onError: err,
    }),
  };
}

export function useCasosLigados(d: Destino) {
  return useQuery({
    queryKey: ['contenido', 'casos', d.contentType, d.contentId],
    queryFn: () =>
      unwrap<CasoLigado[]>(
        api.GET('/api/v1/admin/content-links', {
          params: { query: { contentType: d.contentType, contentId: d.contentId } },
        }),
      ),
  });
}

export function useCasosMutaciones(d: Destino) {
  const qc = useQueryClient();
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['contenido'] });
  return {
    ligar: useMutation({
      mutationFn: (questionId: number) =>
        unwrap(api.POST('/api/v1/admin/content-links', { body: { ...d, questionId } })),
      onSuccess: () => {
        refrescar();
        toast.success('Caso ligado.');
      },
      onError: err,
    }),
    desligar: useMutation({
      mutationFn: (id: number) =>
        unwrap(
          api.DELETE('/api/v1/admin/content-links/{id}', {
            params: { path: { id: String(id) } },
          }),
        ),
      onSuccess: refrescar,
      onError: err,
    }),
  };
}
